import { formatUTCDateString } from 'ui-smartforce-settings';
import { CallbackDataParams } from 'echarts/types/dist/shared';
import { SFGrey, SFTextBlack, SFTextWhite } from 'sfui';
import moment from 'moment';
import { MasonryElementSize } from '../Components/Masonry/MasonryElement/MasonryElement';
import {
  AnalyticsFiltersForm,
  AnalyticsPeriod,
  AnalyticsPeriodOption,
  AnalyticsPeriodOptions,
  Widget
} from '../Models';

interface WidgetComponentSizeDictType {
  [key: string]: MasonryElementSize;
}

const widgetComponentSizeDict: WidgetComponentSizeDictType = {
  rate_card: 'xs',
  highlight_card: 'xs',
  metric_card: 'xs',
  ranking_card: 'sm',
  doughnut: 'sm',
  gradient_line: 'md',
  heat_map: 'md',
  cluster_map: 'md',
  sankey: 'md',
  filter_map: 'lg',
  pattern_map: 'lg',
  grouped_bar: 'lg',
  stacked_line: 'lg'
};

export function getWidgetColSize(componentName: string): MasonryElementSize {
  return widgetComponentSizeDict[componentName];
}

export function onShareWidgetClone(
  element: HTMLElement,
  changeWidth: boolean
): void {
  // Add class to adjust element layout
  element.classList.add('SFAnalytics-ShareImage');
  element.style.height = 'auto';

  if (changeWidth) {
    element.style.width = 'fit-content';
  }

  //Remove map buttons
  const map = element.querySelector('div[class*=GoogleMap_module_googleMap]');

  if (map) {
    const controls: NodeListOf<Element> = map.querySelectorAll('div.gmnoprint');
    controls.forEach((e: Element) => e.remove());
  }
}

export function isMapWidget(widget: Widget): boolean {
  return ['heat_map', 'cluster_map', 'pattern_map', 'filter_map'].includes(
    widget.component.name
  );
}

export function getPeriodString(period: AnalyticsPeriod): string {
  const dateFormat: string = 'MMM D, YYYY';

  return `
    ${formatUTCDateString(period.from, dateFormat)}
    ${' - '}
    ${formatUTCDateString(period.to, dateFormat)}
  `;
}

export function getDateChipLabel(
  filters: AnalyticsFiltersForm,
  period?: AnalyticsPeriod,
  periodOptions?: AnalyticsPeriodOptions
): string {
  if (
    filters.period === 'custom' &&
    filters.dateRange?.from &&
    filters.dateRange.to
  ) {
    return `${moment(filters.dateRange.from).format('MMM D, YYYY')}-
      ${moment(filters.dateRange.to).format('MMM D, YYYY')}
    `;
  } else if (
    periodOptions &&
    (filters.period === 'quarter' ||
      filters.period === 'half_year' ||
      filters.period === 'year')
  ) {
    const filterLabel =
      filters.period === 'quarter'
        ? 'Quarter'
        : filters.period === 'half_year'
          ? 'Half-year'
          : 'Year';

    const filterValue = periodOptions[filters.period].find(
      (o) => o.value === filters.periodValue
    ) as AnalyticsPeriodOption;

    return `${filterLabel}: ${filterValue?.label}`;
  } else {
    const periodString = period ? `: ${getPeriodString(period)}` : '';
    const periodLabel = filters.period === 'week' ? 'Last week' : 'Last month';
    return periodLabel + periodString;
  }
}

function getTooltipTitleNode(text: string, dayMode: boolean): HTMLSpanElement {
  const span = document.createElement('p');
  span.innerHTML = text;
  span.style.padding = '0';
  span.style.margin = '0';
  span.style.fontFamily = 'Roboto';
  span.style.fontSize = '13px';
  span.style.fontStyle = 'normal';
  span.style.fontWeight = '500';
  span.style.lineHeight = '22px';
  span.style.color = dayMode ? SFTextWhite : SFTextBlack;
  return span;
}

function getTooltipTextNode(text: string, dayMode: boolean): HTMLSpanElement {
  const span = document.createElement('p');
  span.innerHTML = text;
  span.style.padding = '0';
  span.style.margin = '0';
  span.style.fontFamily = 'Roboto';
  span.style.fontSize = '12px';
  span.style.fontStyle = 'normal';
  span.style.fontWeight = '400';
  span.style.lineHeight = '18px';
  span.style.letterSpacing = '0.24';
  span.style.color = dayMode ? SFGrey[300] : SFGrey[700];
  return span;
}

function getListItem(
  item: CallbackDataParams,
  data_label: string,
  data_label_singular: string,
  dayMode: boolean,
  withBorder: boolean
): HTMLLIElement {
  const li = document.createElement('li');
  li.style.padding = '6px 0';
  li.style.display = 'flex';
  li.style.gap = '6px';

  if (withBorder) {
    li.style.borderBottom = `1px solid ${dayMode ? SFGrey[700] : SFGrey[300]}`;
  }

  const colorBorder = document.createElement('div');
  colorBorder.style.width = '2px';
  colorBorder.style.height = '40px';
  colorBorder.style.width = '2px';
  colorBorder.style.backgroundColor = item.color as string;

  const textContainer = document.createElement('div');
  textContainer.style.display = 'flex';
  textContainer.style.flexDirection = 'column';
  textContainer.style.alignItems = 'flex-start';
  textContainer.appendChild(
    getTooltipTitleNode(item.seriesName as string, dayMode)
  );
  textContainer.appendChild(
    getTooltipTextNode(
      `${item.value} ${item.value === 1 ? data_label_singular : data_label}`,
      dayMode
    )
  );

  li.appendChild(colorBorder);
  li.appendChild(textContainer);
  return li;
}

export function tooltipFormatter(
  parameters: CallbackDataParams[],
  data_label: string,
  data_label_singular: string,
  dayMode: boolean
) {
  const container = document.createElement('div');
  container.style.padding = '5px 12px';
  container.style.borderRadius = '2px';

  const titleName = parameters
    .reduce((a, b) => a + (b.value as number), 0)
    .toString();

  const title = getTooltipTitleNode(titleName, dayMode);
  title.style.paddingBottom = '12px';
  title.style.textAlign = 'left';
  container.appendChild(title);

  const hr = document.createElement('hr');
  hr.style.border = '0';
  hr.style.borderBottom = `2px solid ${dayMode ? SFGrey[700] : SFGrey[300]}`;
  hr.style.padding = '0px';
  hr.style.margin = '0px';

  container.appendChild(hr);

  const list = document.createElement('ul');
  list.style.padding = '0px';
  list.style.margin = '0px';
  list.style.paddingTop = '6px';
  list.style.display = 'flex';
  list.style.flexDirection = 'column';
  list.style.listStyle = 'none';

  parameters.forEach((param, index) =>
    list.appendChild(
      getListItem(
        param,
        data_label,
        data_label_singular,
        dayMode,
        index < parameters.length - 1
      )
    )
  );

  container.appendChild(list);

  return container;
}
